import React from 'react';
import { isMobile, isTablet } from "react-device-detect";
import styles from './skeleton.module.css';

// Base skeleton components
interface SkeletonLineProps {
  width?: string;
  height?: string;
  margin?: string;
  className?: string;
}

interface SkeletonCircleProps {
  size?: string;
  className?: string;
}

interface SkeletonRectangleProps {
  width?: string;
  height?: string;
  margin?: string;
  className?: string;
}

export const SkeletonLine: React.FC<SkeletonLineProps> = ({ 
  width, 
  height, 
  margin, 
  className = '' 
}) => (
  <div 
    className={`${styles.skeletonLine} ${className}`}
    style={{ width, height, margin }}
  />
);

export const SkeletonCircle: React.FC<SkeletonCircleProps> = ({ 
  size, 
  className = '' 
}) => (
  <div 
    className={`${styles.skeletonCircle} ${className}`}
    style={{ width: size, height: size }}
  />
);

export const SkeletonRectangle: React.FC<SkeletonRectangleProps> = ({ 
  width, 
  height, 
  margin, 
  className = '' 
}) => (
  <div 
    className={`${styles.skeletonRectangle} ${className}`}
    style={{ width, height, margin }}
  />
);

// Sidebar skeleton
export const SidebarSkeleton: React.FC = () => (
  <div className={styles.sidebarContainer}>
    {/* App logo - half width rectangle at top left */}
    <div style={{ marginBottom: '12px' }}>
      <SkeletonRectangle width="50%" height="32px" margin="0" />
    </div>

    {/* Text line below logo - half length */}
    <div style={{ marginBottom: '24px' }}>
      <SkeletonLine width="50%" height="14px" margin="0" />
    </div>
    
    {/* Full length accordion/dropdown skeleton */}
    <div style={{ marginBottom: '16px' }}>
      <SkeletonRectangle width="100%" height="48px" margin="0" />
    </div>

    {/* Section divider */}
    <div style={{ height: '1px', background: 'rgba(255, 255, 255, 0.1)', marginBottom: '16px' }}></div>

    {/* Menu items - 6-7 rectangle skeletons */}
    {[...Array(7)].map((_, index) => (
      <div key={index} style={{ marginBottom: '8px' }}>
        <SkeletonRectangle width="100%" height="40px" margin="0" />
      </div>
    ))}
    
    {/* User profile skeleton at bottom - kept as is */}
    <div className={styles.userProfileContainer} style={{ marginTop: 'auto', paddingTop: '20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <SkeletonCircle size="48px" />
        <div className={styles.userInfoContainer} style={{ marginLeft: '12px' }}>
          <SkeletonLine width="100px" height="16px" margin="0 0 4px 0" />
          <SkeletonLine width="80px" height="14px" margin="0" />
        </div>
      </div>
      {/* Logout button */}
      {/* <SkeletonRectangle width="100%" height="36px" margin="8px 0 0 0" /> */}
    </div>
  </div>
);

// Mobile default skeleton - specific mobile content structure
export const MobileDefaultSkeleton: React.FC = () => (
  <div>
    {/* Project status - 40% width */}
    <div style={{ marginBottom: '12px' }}>
      <SkeletonLine width="40%" height="14px" margin="0" />
    </div>

    {/* Project name - 60% width rectangular */}
    <div style={{ marginBottom: '16px' }}>
      <SkeletonRectangle width="60%" height="24px" margin="0" />
    </div>

    {/* Another text skeleton */}
    <div style={{ marginBottom: '20px' }}>
      <SkeletonLine width="50%" height="14px" margin="0" />
    </div>

    {/* Section skeleton - 86px height with 12px gap */}
    <div style={{ marginBottom: '12px' }}>
      <SkeletonRectangle width="100%" height="86px" margin="0" />
    </div>

    {/* Tab headers line skeleton */}
    <div style={{ marginBottom: '16px' }}>
      <SkeletonLine width="100%" height="40px" margin="0" />
    </div>

    {/* Header + 8 rows skeleton */}
    <div>
      {/* Table header */}
      <div style={{ marginBottom: '8px' }}>
        <SkeletonRectangle width="100%" height="39px" margin="0" />
      </div>
      
      {/* 8 table rows */}
      {[...Array(8)].map((_, index) => (
        <div key={index} style={{ marginBottom: '4px' }}>
          <SkeletonRectangle width="100%" height="39px" margin="0" />
        </div>
      ))}
    </div>
  </div>
);

// Mobile header skeleton
export const MobileHeaderSkeleton: React.FC = () => (
  <div className={styles.mobileHeaderContainer}>
    {/* Mobile header bar */}
    <div className={styles.mobileHeaderBar}>
      {/* Menu/hamburger button */}
      <SkeletonRectangle width="24px" height="24px" margin="0" />
      
      {/* Logo in center */}
      {/* <SkeletonRectangle width="120px" height="28px" margin="0" /> */}
      
      {/* Right side placeholder */}
      <div style={{ width: '24px' }}></div>
    </div>
  </div>
);

// Table skeleton
export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({ 
  rows = 10, 
  columns = 4 
}) => (
  <div className={styles.tableContainer}>
    {/* Listing title - top left */}
    <div style={{ marginBottom: '16px' }}>
      <SkeletonLine width="25%" height="21px" margin="0" />
    </div>
    
    {/* Filter section - search field and expand button */}
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px', gap: '12px' }}>
      <SkeletonRectangle width="300px" height="39px" margin="0" />
      <SkeletonRectangle width="120px" height="39px" margin="0" />
    </div>
    
    {/* Selected filters section - full rectangular line */}
    <div style={{ marginBottom: '36px' }}>
      <SkeletonRectangle width="100%" height="32px" margin="0" />
    </div>
    
    {/* Table header - full width with 39px height */}
    <div style={{ marginBottom: '8px' }}>
      <SkeletonRectangle width="100%" height="44px" margin="0" />
    </div>
    
    {/* Table rows - same height as header (39px) */}
    {[...Array(rows)].map((_, rowIndex) => (
      <div key={rowIndex} style={{ marginBottom: '2px' }}>
        <SkeletonRectangle width="100%" height="48px" margin="0" />
      </div>
    ))}
    
    {/* Pagination skeleton */}
    <div className={styles.paginationContainer}>
      <SkeletonLine width="150px" height="16px" margin="0" />
      <div className={styles.paginationButtons}>
        {[...Array(3)].map((_, index) => (
          <SkeletonCircle key={index} size="32px" />
        ))}
      </div>
    </div>
  </div>
);

// Dashboard card skeleton
export const CardSkeleton: React.FC<{ height?: string }> = ({ height = '155px' }) => (
  <div className={styles.cardContainer}>
    <SkeletonLine width="60%" height="18px" margin="0 0 12px 0" />
    <SkeletonRectangle width="100%" height={height} margin="0 0 12px 0" />
    <div className={styles.flexSpaceBetween}>
      <SkeletonLine width="30%" height="12px" margin="0" />
      <SkeletonLine width="20%" height="12px" margin="0" />
    </div>
  </div>
);

// Form skeleton
export const FormSkeleton: React.FC = () => (
  <div className={styles.formContainer}>
    <SkeletonLine width="40%" height="21px" margin="0 0 24px 0" />
    
    {[...Array(8)].map((_, index) => (
      <div key={index} className={styles.formFieldContainer}>
        <SkeletonLine width="25%" height="15px" margin="0 0 6px 0" />
        <SkeletonRectangle width="100%" height="39px" margin="0" />
      </div>
    ))}
    
    <div className={styles.formButtonsContainer}>
      <SkeletonRectangle width="120px" height="39px" margin="0" />
      <SkeletonRectangle width="120px" height="39px" margin="0" />
    </div>
  </div>
);

// Dashboard layout skeleton
export const DashboardSkeleton: React.FC = () => (
  <div className={styles.dashboardContainer}>
    {/* Left section - 63% width */}
    <div style={{ width: '63%', display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <SkeletonLine width="30%" height="21px" margin="0 0 24px 0" />
      
      {/* Top section - 40% of available height */}
      <div className={styles.cardContainer} style={{ height: '40%', marginBottom: '24px', overflow: 'hidden' }}>
        {/* Section title */}
        <SkeletonLine width="25%" height="18px" margin="0 0 16px 0" />
        
        {/* Filter section */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px', gap: '8px' }}>
          <SkeletonRectangle width="60%" height="32px" margin="0" />
          <SkeletonRectangle width="25%" height="32px" margin="0" />
        </div>
        
        {/* Table header */}
        <SkeletonRectangle width="100%" height="32px" margin="0 0 4px 0" />
        
        {/* Table rows */}
        {[...Array(6)].map((_, rowIndex) => (
          <div key={rowIndex} style={{ marginBottom: '2px' }}>
            <SkeletonRectangle width="100%" height="32px" margin="0" />
          </div>
        ))}
      </div>
      
      {/* Bottom section - 45% of available height */}
      <div className={styles.cardContainer} style={{ height: '45%', overflow: 'hidden' }}>
        {/* Section title */}
        <SkeletonLine width="20%" height="18px" margin="0 0 16px 0" />
        
        {/* Filter section */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px', gap: '8px' }}>
          <SkeletonRectangle width="65%" height="32px" margin="0" />
          <SkeletonRectangle width="20%" height="32px" margin="0" />
        </div>
        
        {/* Table header */}
        <SkeletonRectangle width="100%" height="32px" margin="0 0 4px 0" />
        
        {/* Table rows */}
        {[...Array(9)].map((_, rowIndex) => (
          <div key={rowIndex} style={{ marginBottom: '2px' }}>
            <SkeletonRectangle width="100%" height="32px" margin="0" />
          </div>
        ))}
      </div>
    </div>
    
    {/* Right section - 36% max width, full height */}
    <div style={{ width: '36%', height: '100vh', marginLeft: '1%' }}>
      {/* Tabs skeleton */}
      <div className={styles.tabsContainer}>
        <SkeletonLine width="80px" height="15px" margin="0 24px 12px 0" />
        <SkeletonLine width="120px" height="15px" margin="0 0 12px 0" />
      </div>
      
      {/* Full height content area */}
      <CardSkeleton height="calc(100vh - 220px)" />
    </div>
  </div>
);

// chart section
export const ChartSectionSkeleton: React.FC = () => (
  <>
    {/* Chart title */}
    <div style={{ marginBottom: '16px' }}>
        <SkeletonLine width="30%" height="18px" margin="0" />
    </div>
    
    {/* Chart area - full width, 500px height */}
    <div className={styles.cardContainer}>
        <SkeletonRectangle width="100%" height="320px" margin="0 0 12px 0" />
        <div className={styles.flexSpaceBetween}>
            <SkeletonLine width="30%" height="12px" margin="0" />
            <SkeletonLine width="20%" height="12px" margin="0" />
        </div>
    </div>
  </>
);

// Charts skeleton
export const ChartSkeleton: React.FC = () => (
  <div className={styles.contentArea}>
    {/* Top row - Title (left) and Last Update (right) */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
      <SkeletonLine width="25%" height="21px" margin="0" />
      <SkeletonLine width="15%" height="15px" margin="0" />
    </div>
    
    {/* Filters section - full width rectangular */}
    <div style={{ marginBottom: '32px' }}>
      <SkeletonRectangle width="100%" height="48px" margin="0" />
    </div>

    <ChartSectionSkeleton/>
    <ChartSectionSkeleton/>
  </div>
);

// Default skeleton
export const DefaultSkeleton: React.FC = () => (
    <div>
        <SkeletonLine width="40%" height="21px" margin="0 0 24px 0" />
        <div className={styles.gridContainer}>
        <CardSkeleton />
        <CardSkeleton />
        </div>
        <TableSkeleton rows={10} columns={4} />
    </div>
);
// Main application skeleton - content area only (responsive for mobile/desktop)
export const ApplicationSkeleton: React.FC<{ type?: 'dashboard' | 'table' | 'form' | 'charts' | 'default' }> = ({ 
  type = 'default' 
}) => {
  const isMobileView = isMobile && !isTablet;
  
  // For mobile, use consistent padding with mobileContentArea
  // For desktop, use standard content area styling
  const containerStyle = isMobileView 
    ? { padding: '24px 16px', minHeight: '100%' } // Match mobileContentArea padding
    : {};
  
  return (
    <div className={styles.contentArea} style={containerStyle}>
      {type === 'dashboard' && <DashboardSkeleton />}
      {type === 'table' && <TableSkeleton rows={12} columns={5} />}
      {type === 'form' && <FormSkeleton />}
      {type === 'charts' && <ChartSkeleton />}
      {type === 'default' && (isMobileView ? <MobileDefaultSkeleton /> : <DefaultSkeleton />)}
    </div>
  );
};

// Full application skeleton - includes sidebar/mobile header and content (for initial app load)
export const FullApplicationSkeleton: React.FC<{ type?: 'dashboard' | 'table' | 'form' | 'charts' | 'default' }> = ({ 
  type = 'default' 
}) => {
  const isMobileView = isMobile && !isTablet;
  
  if (isMobileView) {
    // Mobile layout - header at top, content below
    return (
      <div className={styles.mobileMainContainer}>
        <MobileHeaderSkeleton />
        <div className={styles.mobileContentArea}>
          {type === 'dashboard' && <DashboardSkeleton />}
          {type === 'table' && <TableSkeleton rows={12} columns={5} />}
          {type === 'form' && <FormSkeleton />}
          {type === 'charts' && <ChartSkeleton />}
          {type === 'default' && <MobileDefaultSkeleton />}
        </div>
      </div>
    );
  }
  
  // Desktop/tablet layout - sidebar + content
  return (
    <div className={styles.mainContentContainer}>
      <SidebarSkeleton />
      <div className={styles.contentArea}>
        {type === 'dashboard' && <DashboardSkeleton />}
        {type === 'table' && <TableSkeleton rows={12} columns={5} />}
        {type === 'form' && <FormSkeleton />}
        {type === 'charts' && <ChartSkeleton />}
        {type === 'default' && (<DefaultSkeleton/>)}
      </div>
    </div>
  );
};
